import type { CSSProperties, FC, ReactNode } from 'react'

import { useId } from 'react'
import { BadgeCheck } from 'lucide-react'
import { Reveal } from '@/components/generals/reveal'
import { LiveDot } from '@/components/generals/live-dot'

const SPIN_STYLE: CSSProperties = {
    transformOrigin: '100px 100px',
    transformBox: 'view-box'
}

const Seal: FC = (): ReactNode => {
    const id = useId().replace(/[^a-zA-Z0-9_-]/g, '')

    return (
        <svg viewBox='0 0 200 200' className='size-52 sm:size-60' role='img' aria-label='Freight Tech 100, class of 2026'>
            <defs>
                <path id={`${id}-ring`} d='M 100 100 m -74 0 a 74 74 0 1 1 148 0 a 74 74 0 1 1 -148 0' />
            </defs>

            <circle cx='100' cy='100' r='98' fill='#2f37ff' />
            <circle cx='100' cy='100' r='88' fill='none' stroke='rgba(255,255,255,0.18)' />

            <g className='animate-spin-slow' style={SPIN_STYLE}>
                <text fontSize='10.6' fill='#ffffff' className='font-mono' aria-hidden='true'>
                    <textPath href={`#${id}-ring`}>
                        FREIGHT TECH 100 · CLASS OF 2026 · FREIGHT TECH 100 · CLASS OF 2026 ·
                    </textPath>
                </text>
            </g>

            <circle cx='100' cy='100' r='58' fill='none' stroke='rgba(255,255,255,0.32)' />

            <text x='100' y='108' textAnchor='middle' fontSize='44' fontWeight='700' letterSpacing='-2' fill='#ffffff' className='font-heading'>
                100
            </text>

            <text x='100' y='130' textAnchor='middle' fontSize='9' letterSpacing='2' fill='rgba(255,255,255,0.7)' className='font-mono'>
                2026
            </text>
        </svg>
    )
}

const REASONS = [
    "One network for 3,400+ carriers, ports and systems",
    "A 24/7 operations desk staffed by people who know freight",
    "Customers live in days, not quarters"
]

/** The Freight Tech 100 recognition, with a slowly turning seal. */
export const FreightTechFeature: FC = (): ReactNode => {
    return (
        <section id='freight-tech-100' aria-labelledby='freight-tech-100-title' className='section-y'>
            <div className='shell'>
                <Reveal className='surface relative isolate overflow-hidden rounded-[28px] p-6 sm:rounded-[36px] sm:p-12'>
                    <div aria-hidden='true' className='pointer-events-none absolute -left-24 -top-24 -z-10 size-[26rem] rounded-full bg-[radial-gradient(closest-side,rgba(201,187,255,0.5),transparent)]' />
                    <div aria-hidden='true' className='pointer-events-none absolute -bottom-32 right-0 -z-10 size-[26rem] rounded-full bg-[radial-gradient(closest-side,rgba(255,184,156,0.45),transparent)]' />

                    <div className='grid items-center gap-10 lg:grid-cols-[auto_1fr] lg:gap-16'>
                        <div className='mx-auto lg:mx-0'>
                            <Seal />
                        </div>

                        <div>
                            <p className='eyebrow flex items-center gap-2 text-stone'>
                                <LiveDot />
                                Recognition
                            </p>

                            <h2 id='freight-tech-100-title' className='headline mt-5 text-[clamp(2.2rem,4.4vw,3.6rem)] text-ink'>
                                <span className='block'>
                                    Named to the 2026
                                </span>

                                <span className='block text-ultramarine'>
                                    Freight Tech 100.
                                </span>
                            </h2>

                            <p className='mt-5 max-w-2xl text-lg leading-relaxed text-ink-soft'>
                                The Freight Tech 100 recognises the companies changing how goods move. This year it highlighted Quayside for pairing an integration platform with a human operations desk, so the exceptions get handled instead of escalated.
                            </p>

                            <ul className='mt-7 grid gap-3 md:grid-cols-3'>
                                {REASONS.map(reason => (
                                    <li key={reason} className='flex gap-2.5 rounded-2xl bg-cream p-4 text-[14px] leading-snug text-ink-soft'>
                                        <BadgeCheck aria-hidden='true' className='size-5 shrink-0 text-ultramarine' strokeWidth={1.8} />
                                        {reason}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    )
}
